import { getDocument } from '@atlaskit/browser-apis';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { NextEditorPlugin } from '@atlaskit/editor-common/types/next-editor-plugin';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { ROVO_SKILL_COMBINED_EXTENSION_KEY } from '@atlassian/conversation-assistant-content-transformer/utils/skills';

const pluginKey = new PluginKey<{ text?: string }>('scheduledChatPlaceholder');

export type ScheduledChatPlaceholderPlugin = NextEditorPlugin<
	'scheduledChatPlaceholder',
	{
		commands: {
			setPlaceholder: (text: string | undefined) => EditorCommand;
		};
	}
>;

export const scheduledChatPlaceholderPlugin: ScheduledChatPlaceholderPlugin = () => ({
	name: 'scheduledChatPlaceholder',
	commands: {
		setPlaceholder:
			(text) =>
			({ tr }) =>
				tr.setMeta(pluginKey, { text }),
	},
	pmPlugins: () => [
		{
			name: 'scheduledChatPlaceholder',
			plugin: () =>
				new SafePlugin<{ text?: string }>({
					key: pluginKey,
					state: {
						init: () => ({}),
						apply: (tr, previous) => tr.getMeta(pluginKey) ?? previous,
					},
					props: {
						decorations(state) {
							const text = pluginKey.getState(state)?.text;
							const paragraph = state.doc.firstChild;
							const skill = paragraph?.firstChild;
							if (
								!text ||
								state.doc.childCount !== 1 ||
								paragraph?.type.name !== 'paragraph' ||
								paragraph.textContent.trim() !== '' ||
								skill?.type.name !== 'inlineExtension' ||
								skill.attrs.extensionKey !== ROVO_SKILL_COMBINED_EXTENSION_KEY ||
								skill.attrs.parameters?.slug !== 'schedule-chat' ||
								paragraph.children.slice(1).some((child) => !child.isText)
							) {
								return undefined;
							}

							return DecorationSet.create(state.doc, [
								Decoration.node(0, paragraph.nodeSize, { class: 'scheduled-chat-placeholder' }),
								Decoration.node(1, 1 + skill.nodeSize, {
									class: 'scheduled-chat-placeholder-skill',
								}),
								Decoration.widget(
									state.doc.content.size - 1,
									() => {
										const document = getDocument();
										if (!document) {
											throw new Error('Document not found');
										}
										const element = document.createElement('span');
										element.className = 'placeholder-decoration';
										element.setAttribute('aria-hidden', 'true');
										element.setAttribute('contenteditable', 'false');
										element.textContent = text;
										return element;
									},
									{ key: text, side: 1 },
								),
							]);
						},
					},
				}),
		},
	],
});
